import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Sparkles, TrendingUp, Calendar, MessageCircle, DollarSign, Loader2, Send,
  ThumbsUp, ThumbsDown, Minus, Copy, Check, CloudRain, Zap
} from 'lucide-react';

export function BusinessInsightsPage() {
  return (
    <div className="space-y-4" data-testid="insights-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-purple-600" />
          Business Insights IA
        </h1>
        <p className="text-muted-foreground text-sm">Prediction, happy hour, Q&R sur tes donnees, simulation de prix</p>
      </div>

      <Tabs defaultValue="predict">
        <TabsList className="flex-wrap">
          <TabsTrigger value="predict" data-testid="tab-predict"><TrendingUp className="w-4 h-4 mr-1" /> Prediction</TabsTrigger>
          <TabsTrigger value="happy" data-testid="tab-happy"><Calendar className="w-4 h-4 mr-1" /> Happy Hour</TabsTrigger>
          <TabsTrigger value="ask" data-testid="tab-ask"><MessageCircle className="w-4 h-4 mr-1" /> Ask My Data</TabsTrigger>
          <TabsTrigger value="price" data-testid="tab-price"><DollarSign className="w-4 h-4 mr-1" /> Simulateur Prix</TabsTrigger>
        </TabsList>

        <TabsContent value="predict"><PredictTab /></TabsContent>
        <TabsContent value="happy"><HappyHourTab /></TabsContent>
        <TabsContent value="ask"><AskTab /></TabsContent>
        <TabsContent value="price"><PriceTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ===== TAB 1: PREDICT =====
function PredictTab() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [nbJours, setNbJours] = useState(3);

  const run = async () => {
    setLoading(true);
    try { const res = await api.post('/insights/predict-sales', { nb_jours: nbJours }); setData(res.data); }
    catch { toast.error('Erreur prediction'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="space-y-1"><Label className="text-xs">Jours a predire</Label>
            <Input type="number" min="1" max="7" value={nbJours} onChange={e => setNbJours(parseInt(e.target.value) || 1)} className="w-24" data-testid="predict-jours" />
          </div>
          <Button onClick={run} disabled={loading} data-testid="predict-btn"><Sparkles className="w-4 h-4 mr-1" /> {loading ? 'Analyse...' : 'Predire'}</Button>
        </div>
      </CardContent></Card>

      {loading && <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>}

      {data && (
        <div className="space-y-3">
          {data.resume && (
            <Card className="border-purple-200 bg-purple-50/30">
              <CardContent className="pt-4 space-y-2">
                <p className="text-sm">💡 {data.resume}</p>
                {data.correlation_meteo && (
                  <p className="text-xs text-muted-foreground flex items-start gap-1">
                    <CloudRain className="w-3 h-3 mt-0.5 flex-shrink-0 text-blue-500" />
                    <span><b>Corrélation météo :</b> {data.correlation_meteo}</span>
                  </p>
                )}
                {data._debug && (
                  <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground pt-1 border-t">
                    <Badge variant="outline" className="text-[9px]">📊 {data._debug.historique_jours}j historique CA</Badge>
                    <Badge variant="outline" className="text-[9px]">🌤 {data._debug.meteo_hist_disponible}j météo analysée</Badge>
                    <Badge variant="outline" className="text-[9px]">🍱 {data._debug.articles_detectes} articles</Badge>
                    <Badge variant="outline" className="text-[9px]">📂 {data._debug.familles_detectees} familles</Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.predictions?.map((p, i) => (
              <Card key={i} className="hover:shadow-md transition">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base capitalize flex items-center justify-between">
                    <span>{p.jour} <span className="text-xs font-normal text-muted-foreground">{p.date}</span></span>
                    {p.confiance && (
                      <Badge variant="outline" className={`text-[10px] ${p.confiance === 'haute' ? 'border-green-500 text-green-700' : p.confiance === 'moyenne' ? 'border-amber-500 text-amber-700' : 'border-red-500 text-red-700'}`}>
                        {p.confiance}
                      </Badge>
                    )}
                  </CardTitle>
                  {p.meteo && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <CloudRain className="w-3 h-3" /> {p.meteo}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div><p className="text-2xl font-bold text-green-600">{p.ca_estime}€</p><p className="text-xs text-muted-foreground">CA estime</p></div>
                    <div><p className="text-2xl font-bold text-blue-600">~{p.nb_clients_estime}</p><p className="text-xs text-muted-foreground">clients</p></div>
                  </div>
                  {p.alerte_meteo && (
                    <div className="p-2 rounded bg-amber-50 border border-amber-200 text-xs flex items-start gap-1">
                      <CloudRain className="w-3 h-3 mt-0.5 text-amber-600 flex-shrink-0" /> <span>{p.alerte_meteo}</span>
                    </div>
                  )}
                  <div className="border-t pt-2 space-y-1">
                    <p className="text-xs font-semibold">Conseils prod :</p>
                    {(p.conseils_prod || []).map((c, ci) => (
                      <p key={ci} className="text-xs text-muted-foreground">• {c}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== TAB 2: HAPPY HOUR =====
function HappyHourTab() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);

  const run = async () => {
    setLoading(true);
    try { const res = await api.post('/insights/happy-hour', {}); setData(res.data); }
    catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const copy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopied(idx);
    toast.success('Copie !');
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-4">
        <Button onClick={run} disabled={loading} data-testid="happy-btn"><Sparkles className="w-4 h-4 mr-1" /> {loading ? 'Analyse...' : 'Detecter creneaux creux + generer promos'}</Button>
      </CardContent></Card>

      {loading && <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>}

      {data && (
        <>
          {data.analyse && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="pt-4"><p className="text-sm">📊 {data.analyse}</p></CardContent>
            </Card>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {data.promos?.map((p, i) => (
              <Card key={i} className="border-orange-200 hover:shadow-md transition">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge className="bg-orange-100 text-orange-700 capitalize">{p.jour} • {p.creneau}</Badge>
                      <CardTitle className="text-base mt-2">{p.titre}</CardTitle>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-red-500">-{p.reduction_pct}%</p>
                      <p className="text-[10px] text-muted-foreground">impact: +{p.impact_estime_euros_semaine}€/sem</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm">{p.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {(p.produits_cibles || []).map((pr, pi) => <Badge key={pi} variant="outline" className="text-xs">{pr}</Badge>)}
                  </div>
                  <div className="border-t pt-3">
                    <Label className="text-xs text-muted-foreground">Texte Instagram pret</Label>
                    <div className="mt-1 p-2 rounded bg-muted/40 border text-sm italic">{p.texte_instagram}</div>
                    <Button size="sm" variant="outline" onClick={() => copy(p.texte_instagram, i)} className="mt-2" data-testid={`copy-promo-${i}`}>
                      {copied === i ? <Check className="w-3 h-3 mr-1 text-green-600" /> : <Copy className="w-3 h-3 mr-1" />}
                      {copied === i ? 'Copie' : 'Copier'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ===== TAB 3: ASK MY DATA =====
function AskTab() {
  const { api } = useAuth();
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionIdRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const ask = async () => {
    const q = question.trim();
    if (!q) return;
    setLoading(true);
    setMessages(m => [...m, { role: 'user', text: q }]);
    setQuestion('');
    try {
      const res = await api.post('/insights/ask-data', { question: q, session_id: sessionIdRef.current });
      sessionIdRef.current = res.data.session_id;
      setMessages(m => [...m, { role: 'ai', text: res.data.reponse }]);
    } catch {
      toast.error('Erreur IA');
      setMessages(m => [...m, { role: 'ai', text: 'Erreur, reessaye.' }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    'Quel est mon meilleur jour ce mois-ci ?',
    'Quel plat est le plus rentable ?',
    'Combien j\'ai gaspille en valeur ce mois ?',
    'Ai-je un probleme de stock a surveiller ?',
    'Quel est l\'impact de mes nouveaux clients ?',
  ];

  return (
    <Card className="h-[600px] flex flex-col">
      <CardHeader className="pb-2 border-b">
        <CardTitle className="text-base flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-purple-600" />
          Assistant IA — Interroge tes donnees
        </CardTitle>
        <CardDescription>Pose n'importe quelle question business, l'IA a acces a ton CA, stock, plats, depenses, fidelite.</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden flex flex-col p-0">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-area">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground text-center py-4">Commence avec une de ces questions :</p>
              <div className="grid md:grid-cols-2 gap-2">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => setQuestion(s)} className="text-left p-2.5 rounded border text-xs hover:bg-muted/50 transition" data-testid={`suggestion-${i}`}>
                    💡 {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.role === 'user' ? 'bg-purple-600 text-white' : 'bg-muted'}`}>
                <p className="text-sm whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl px-4 py-2.5 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Analyse des donnees...</span>
              </div>
            </div>
          )}
        </div>
        <div className="border-t p-3 flex gap-2">
          <Input
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !loading) ask(); }}
            placeholder="Pose ta question..."
            data-testid="ask-input"
          />
          <Button onClick={ask} disabled={loading || !question.trim()} data-testid="ask-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ===== TAB 4: PRICE SIMULATOR =====
function PriceTab() {
  const { api } = useAuth();
  const [plats, setPlats] = useState([]);
  const [platId, setPlatId] = useState('');
  const [nouveauPrix, setNouveauPrix] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPlats = useCallback(async () => {
    try { const res = await api.get('/rentabilite/plats'); setPlats(res.data); }
    catch {}
  }, [api]);

  useEffect(() => { fetchPlats(); }, [fetchPlats]);

  const currentPlat = plats.find(p => p.id === platId);

  const simulate = async () => {
    if (!platId || !nouveauPrix) { toast.error('Plat et prix requis'); return; }
    setLoading(true);
    try {
      const res = await api.post('/insights/simulate-price', { plat_id: platId, nouveau_prix: parseFloat(nouveauPrix) });
      setResult(res.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  const verdictColor = { favorable: 'text-green-600 bg-green-50 border-green-300', neutre: 'text-amber-600 bg-amber-50 border-amber-300', defavorable: 'text-red-600 bg-red-50 border-red-300' };
  const verdictIcon = { favorable: ThumbsUp, neutre: Minus, defavorable: ThumbsDown };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Simuler un changement de prix</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-3">
            <div className="space-y-1"><Label>Plat</Label>
              <Select value={platId} onValueChange={v => { setPlatId(v); const p = plats.find(x => x.id === v); if (p) setNouveauPrix(String(p.prix_vente || '')); }}>
                <SelectTrigger data-testid="price-plat-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {plats.map(p => <SelectItem key={p.id} value={p.id}>{p.nom} ({p.prix_vente}€)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Nouveau prix (€)</Label>
              <Input type="number" step="0.1" value={nouveauPrix} onChange={e => setNouveauPrix(e.target.value)} placeholder="4.5" data-testid="price-new-input" />
            </div>
            <div className="flex items-end">
              <Button onClick={simulate} disabled={loading || !platId || !nouveauPrix} className="w-full" data-testid="simulate-btn">
                <Zap className="w-4 h-4 mr-1" /> {loading ? 'Simulation...' : 'Simuler'}
              </Button>
            </div>
          </div>
          {currentPlat && (
            <p className="text-xs text-muted-foreground mt-3">
              Prix actuel : <b>{currentPlat.prix_vente}€</b> • Ventes mois : <b>{currentPlat.nb_vendus || 0}</b> • Marge : <b>{currentPlat.marge_pct}%</b>
            </p>
          )}
        </CardContent>
      </Card>

      {loading && (
        <Card className="border-purple-200 bg-purple-50/20">
          <CardContent className="py-8 flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
            <div className="text-center space-y-1">
              <p className="font-semibold">Simulation en cours...</p>
              <p className="text-xs text-muted-foreground">L'IA calcule l'elasticite et l'impact sur ta marge (10-20s)</p>
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <>
          {/* Big verdict */}
          <Card className={`border-2 ${verdictColor[result.verdict] || ''}`}>
            <CardContent className="py-4 flex items-center gap-4">
              {(() => { const Icon = verdictIcon[result.verdict] || Minus; return <Icon className="w-12 h-12" />; })()}
              <div className="flex-1">
                <p className="text-xs uppercase text-muted-foreground">Verdict IA</p>
                <p className="text-2xl font-bold capitalize">{result.verdict}</p>
                <p className="text-sm mt-1">{result.analyse_ia}</p>
                {result.recommandation && <p className="text-sm italic mt-2">💡 {result.recommandation}</p>}
              </div>
            </CardContent>
          </Card>

          {/* Impact grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard label="Variation prix" current={`${result.prix_actuel}€`} next={`${result.nouveau_prix}€`} delta={`${result.variation_pct > 0 ? '+' : ''}${result.variation_pct}%`} />
            <MetricCard label="Marge %" current={`${result.marge_actuelle_pct}%`} next={`${result.nouvelle_marge_pct}%`} delta={`${(result.nouvelle_marge_pct - result.marge_actuelle_pct).toFixed(1)}pt`} />
            <MetricCard label="Ventes estimees" current={result.ventes_actuelles} next={result.ventes_estimees} delta={`${result.ventes_estimees - result.ventes_actuelles > 0 ? '+' : ''}${result.ventes_estimees - result.ventes_actuelles}`} />
            <MetricCard label="CA mensuel" current={`${result.ca_actuel}€`} next={`${result.ca_nouveau}€`} delta={`${result.ecart_ca > 0 ? '+' : ''}${result.ecart_ca}€`} important positive={result.ecart_ca >= 0} />
          </div>

          <Card className={result.ecart_marge >= 0 ? 'border-green-300 bg-green-50/30' : 'border-red-300 bg-red-50/30'}>
            <CardContent className="py-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Impact marge totale mensuelle</p>
                <p className={`text-3xl font-bold ${result.ecart_marge >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {result.ecart_marge > 0 ? '+' : ''}{result.ecart_marge}€
                </p>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <p>Avant: {result.marge_totale_actuelle}€</p>
                <p>Apres: {result.marge_totale_nouvelle}€</p>
                <p className="mt-1">Elasticite IA: {result.elasticite_estimee}</p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, current, next, delta, important, positive }) {
  const deltaColor = positive == null ? 'text-muted-foreground' : positive ? 'text-green-600' : 'text-red-600';
  return (
    <Card className={important ? 'border-primary' : ''}>
      <CardContent className="pt-3 pb-2.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="flex items-end gap-2 mt-1">
          <p className="text-sm text-muted-foreground line-through">{current}</p>
          <p className="text-xl font-bold">{next}</p>
        </div>
        <p className={`text-xs font-semibold mt-1 ${deltaColor}`}>{delta}</p>
      </CardContent>
    </Card>
  );
}
